



export const convertDate = (str) => {
  if (!str) return null;

  // 🔥 NEW — If value is a Date object
  if (str instanceof Date) {
    const yyyy = str.getFullYear();
    const mm = String(str.getMonth() + 1).padStart(2, "0");
    const dd = String(str.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // 🔥 IMPORTANT — Convert to string safely (fixes .trim error)
  if (typeof str !== "string") return null;

  if (str.trim() === "") return null;

  // 🟡 ISO datetime → local date
  if (str.includes("T")) {
    const date = new Date(str);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // dd-MM-yyyy → yyyy-MM-dd
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  // yyyy-MM-dd → leave as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return str;
  }

  return null;
};


// ✅ Convert "YYYY-MM-DD" to LOCAL Date object (NO timezone shift)
export const parseISODateToLocalDate = (str) => {
  const iso = convertDate(str); // ✅ use your convertDate
  if (!iso) return null;

  const [yyyy, mm, dd] = iso.split("-").map(Number);
  return new Date(yyyy, mm - 1, dd); // ✅ local midnight
};

// ✅ Format "YYYY-MM-DD" safely to dd/MM/yyyy for UI
export const formatISOToDDMMYYYY = (str) => {
  const iso = convertDate(str);
  if (!iso) return "-";

  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Format a date string for display in dd-MM-yyyy format.
 * Expects input in yyyy-MM-dd, returns dd-MM-yyyy.
 */
export const formatDateToDDMMYYYY = (str) => {
  if (!str || str.trim() === "") return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [yyyy, mm, dd] = str.split("-");
    return `${dd}-${mm}-${yyyy}`;
  }

  return str;
};

/**
 * Convert from dd-MM-yyyy to yyyy-MM-dd (ISO).
 * Return null if no value, so DB never gets "".
 */
export const convertDDMMYYYYtoISO = (str) => {
  if (!str || str.trim() === "") return null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  return str;
};

/**
 * Convert a date string to datetime string (yyyy-MM-dd HH:mm:ss)
 */
export const convertToDateTimeString = (str) => {
  if (!str || str.trim() === "") return null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split("-");
    return `${yyyy}-${mm}-${dd} 00:00:00`;
  }

  if (str.includes("T")) {
    return str.split("T")[0] + " 00:00:00";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return `${str} 00:00:00`;
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) {
    return str;
  }

  return null;
};


/**
 * Calculate total days, total decimal hours, and separate hours & minutes between two date/time pairs.
 * Returns null if inputs are invalid or end date/time is before start.
 * 
 * @param {string} fromDateStr - Start date string (ISO or parseable)
 * @param {string} fromTimeStr - Start time string in "HH:mm" 24-hour format
 * @param {string} toDateStr - End date string (ISO or parseable)
 * @param {string} toTimeStr - End time string in "HH:mm" 24-hour format
 * 
 * @returns {Object|null} - { totalDays: number, totalHours: number, hours: number, minutes: number } or null if invalid
 */

// export const calculateTotalDuration = (fromDateStr, fromTimeStr, toDateStr, toTimeStr) => {
//   if (!fromDateStr || !toDateStr) return null;

//   const combineDateAndTime = (dateStr, timeStr) => {
//     const [hour, minute] = timeStr ? timeStr.split(":").map(Number) : [0, 0];
//     const date = new Date(dateStr);
//     if (isNaN(date)) return null;
//     date.setHours(hour, minute, 0, 0);
//     return date;
//   };

//   const start = combineDateAndTime(fromDateStr, fromTimeStr);
//   const end = combineDateAndTime(toDateStr, toTimeStr);
//   if (!start || !end || end < start) return null;

//   const diffMs = end - start;
//   const totalHours = diffMs / (1000 * 60 * 60);
//   const totalDaysRaw = Math.floor(totalHours / 24);
//   const totalDays = totalDaysRaw === 0 ? 1 : totalDaysRaw;

//   const hours = Math.floor(totalHours);
//   const minutes = Math.round((totalHours - hours) * 60);

//   return { totalDays, totalHours, hours, minutes };
// };

export const calculateTotalDuration = (
  fromDateStr,
  fromTimeStr,
  toDateStr,
  toTimeStr
) => {
  if (!fromDateStr || !toDateStr) return null;

  const combineDateAndTime = (dateStr, timeStr) => {
    const [hour, minute] = timeStr
      ? timeStr.split(":").map(Number)
      : [0, 0];

    const date = new Date(dateStr);

    if (isNaN(date)) return null;

    date.setHours(hour || 0, minute || 0, 0, 0);

    return date;
  };

  const start = combineDateAndTime(fromDateStr, fromTimeStr);
  const end = combineDateAndTime(toDateStr, toTimeStr);

  if (!start || !end || end < start) return null;

  const diffMs = end.getTime() - start.getTime();

  // Exact duration in whole minutes
  const totalMinutes = Math.round(diffMs / (1000 * 60));

  const totalHours = totalMinutes / 60;

  const totalDaysRaw = Math.floor(totalMinutes / (24 * 60));
  const totalDays = totalDaysRaw === 0 ? 1 : totalDaysRaw;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    totalDays,
    totalMinutes,
    totalHours,
    hours,
    minutes,
  };
};


/**
 * Calculate extra hours by subtracting slab hours from usage hours.
 * Returns 0 if usage is less than or equal to slab hours.
 *
 * @param {number} slabHours - Allowed slab hours (e.g., 8)
 * @param {number} usageHours - Actual usage in decimal hours (e.g., 10.83)
 *
 * @returns {Object} - { extraHours: number, hours: number, minutes: number }
 */
export const calculateExtraHours = (slabHours, usageHours) => {
  const extra = usageHours > slabHours ? usageHours - slabHours : 0;

  const hours = Math.floor(extra);
  const minutes = Math.round((extra - hours) * 60);

  return { extraHours: extra, hours, minutes };
};


/**
 * Converts decimal hours (e.g. 15.5) to "HH:mm" string for UI display.
 *
 * @param {number} decimalHours - Decimal hours like 15.5
 * @returns {string} Hours and minutes formatted as "HH:mm"
 */
export const decimalHoursToHHMM = (decimalHours) => {
  if (decimalHours == null || isNaN(decimalHours)) return "0:00";
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  const pad = (n) => (n < 10 ? "0" + n : n);
  // Return hours as number (no pad), minutes always zero-padded
  return `${hours}:${pad(minutes)}`;
};


/**
 * Converts "HH:mm" string back to decimal hours.
 *
 * @param {string} hhmm - Time string like "15:30"
 * @returns {number} Decimal hours like 15.5
 */
// export const hhmmToDecimalHours = (hhmm) => {
//   if (!hhmm) return 0;
//   const parts = hhmm.split(":");
//   if (parts.length !== 2) return 0;
//   const hours = parseInt(parts[0], 10);
//   const minutes = parseInt(parts[1], 10);
//   if (isNaN(hours) || isNaN(minutes)) return 0;
//   return +(hours + minutes / 60).toFixed(2);
// };

// export const hhmmToDecimalHours = (hhmm) => {
//   if (!hhmm) return 0;
//   if (typeof hhmm === "number") return hhmm; // already decimal
//   const [h, m] = hhmm.split(":").map(Number);
//   return h + m / 60;
// };

export const hhmmToDecimalHours = (hhmm) => {
  if (hhmm == null || hhmm === "") return 0;
  if (typeof hhmm === "number") return isNaN(hhmm) ? 0 : hhmm;

  const parts = hhmm.split(":");
  const h = parseFloat(parts[0]);
  const m = parseFloat(parts[1]) || 0;

  // Handle invalid numbers safely
  if (isNaN(h) || isNaN(m)) return 0;

  return h + m / 60;
};


/**
 * Convert grace decimal (e.g. 0.30) to "HH:mm" string (e.g. "0:30")
 * Assumes grace decimal represents minutes as fractional part directly.
 *
 * @param {number|string} graceDecimal - Grace time in decimal form like 0.30 or "0.30"
 * @returns {string} Grace time formatted as "H:mm"
 */
export const graceDecimalToHHMM = (graceDecimal) => {
  if (graceDecimal == null) return "0:00";
  const str = graceDecimal.toString();
  const parts = str.split(".");
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parts[1] ? parseInt(parts[1].padEnd(2, "0"), 10) : 0;

  return `${hours}:${minutes < 10 ? "0" + minutes : minutes}`;
};

// src/utils/dateUtils.js

/**
 * Convert local JS Date to UTC ISO string (for sending to backend)
 * @param {Date | string} date
 * @returns {string | null} UTC ISO string
 */
export function toUTC(date) {
  if (!date) return null;

  // If already a string in YYYY-MM-DD format, return as-is
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) return date;

  const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return utcDate.toISOString(); // e.g., "2025-10-21T18:30:00.000Z"
}

/**
 * Convert UTC date string from backend to local JS Date
 * @param {string} utcDateStr
 * @returns {Date}
 */
export function toLocalDate(utcDateStr) {
  if (!utcDateStr) return null;
  return new Date(utcDateStr); // JS automatically converts from UTC to local
}

/**
 * Format Date object for display (local timezone)
 * e.g., "22-10-2025" or "22/10/2025"
 * @param {Date} date
 * @param {string} locale default: 'en-GB'
 * @param {object} options Intl.DateTimeFormat options
 */
export function formatDate(date, locale = "en-GB", options = {}) {
  if (!date) return "";
  return new Intl.DateTimeFormat(locale, { ...options }).format(date);
}

/**
 * Combine local date and time (HH:mm) into a UTC ISO string
 * @param {Date} date - JS Date object from MyDatePicker
 * @param {string} time - "HH:mm" string
 * @returns {string} UTC ISO string
 */
export function combineDateTimeToUTC(date, time) {
  if (!date || !time) return null;

  const [hours, minutes] = time.split(":").map(Number);
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);

  return toUTC(combined); // converts local datetime to UTC
}

export function parseLocalDateStringToDate(dateString) {
  if (!dateString || typeof dateString !== "string") return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null; // must be "YYYY-MM-DD"
  const [year, month, day] = dateString.split('-').map(Number);
  const dt = new Date(year, month - 1, day);
  return isNaN(dt) ? null : dt;
}

export function dateToISODateString(date) {
  if (!date) return null;
  if (typeof date === 'string') return date; // already string
  if (date instanceof Date) {
    return date.toISOString().slice(0, 10);
  }
  return null;
}

export function formatTimeToHHMM(timeStr) {
  if (!timeStr) return "";
  const [hh, mm] = timeStr.split(":");
  return `${hh}:${mm || "00"}`;
}



export const normalizeDateAny = (str) => {
  if (!str) return "";

  // Case 1: JS Date object
  if (str instanceof Date && !isNaN(str)) {
    return str.toISOString().slice(0, 10);
  }

  // Ensure string
  if (typeof str !== "string") return "";

  const s = str.trim();
  if (!s) return "";

  // Case 2: ISO datetime: 2025-10-27T18:30:00.000Z
  if (s.includes("T")) {
    const d = new Date(s);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  }

  // Case 3: yyyy-MM-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return s;
  }

  // Case 4: dd-MM-yyyy
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Case 5: dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
};
